<template>
  <button
    class="road-node flex flex-col items-center transition-transform duration-150"
    :class="[
      lesson.status === 'locked' ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:scale-105',
      lesson.status === 'current' ? 'scale-105' : ''
    ]"
    :title="lesson.status === 'locked' ? lesson.lockMessage : ''"
    :data-lesson-status="lesson.status"
    @click="$emit('select', lesson)"
  >
    <!-- Arrow signpost -->
    <svg :width="nodeWidth" :height="nodeHeight" :viewBox="`0 0 ${nodeWidth} ${nodeHeight}`" class="drop-shadow-md">
      <!-- Arrow shape pointing right or left based on side -->
      <path
        :d="arrowPath"
        :fill="fillColor"
        :stroke="strokeColor"
        stroke-width="2"
      />
      <!-- Lesson number or icon -->
      <text
        :x="textX"
        :y="nodeHeight / 2 + 1"
        text-anchor="middle"
        dominant-baseline="middle"
        class="font-bold"
        :fill="textColor"
        :font-size="12"
      >{{ displayText }}</text>
    </svg>

    <!-- Stars -->
    <div class="flex gap-0.5 mt-0.5">
      <MTIcon
        v-for="i in 3"
        :key="i"
        name="star"
        :size="10"
        :class="i <= lesson.stars ? 'text-yellow-400' : 'text-gray-300'"
      />
    </div>

    <!-- Label -->
    <div
      class="text-[10px] text-center mt-0.5 leading-tight line-clamp-2 max-w-[100px]"
      :class="lesson.status === 'locked' ? 'text-red-600 font-bold' : 'text-gray-700 font-semibold'"
    >
      {{ lesson.title }}
    </div>
  </button>
</template>

<script setup>
import { computed } from 'vue';
import MTIcon from '@/components/ui/MTIcon.vue';

const props = defineProps({
  lesson: { type: Object, required: true },
  index: { type: Number, required: true },
  side: { type: String, default: 'right' } // 'left' or 'right' - arrow direction
});

defineEmits(['select']);

const nodeWidth = 110;
const nodeHeight = 36;
const arrowTip = 16;

// Arrow pointing left or right
const arrowPath = computed(() => {
  const w = nodeWidth;
  const h = nodeHeight;
  const t = arrowTip;
  if (props.side === 'right') {
    // Arrow pointing right: flat left, pointy right
    return `M 0,2 L ${w - t},2 L ${w},${h / 2} L ${w - t},${h - 2} L 0,${h - 2} Q 2,${h / 2} 0,2 Z`;
  }
  // Arrow pointing left: pointy left, flat right
  return `M ${t},2 L ${w},2 Q ${w - 2},${h / 2} ${w},${h - 2} L ${t},${h - 2} L 0,${h / 2} Z`;
});

const textX = computed(() => {
  return props.side === 'right' ? (nodeWidth - arrowTip) / 2 : arrowTip + (nodeWidth - arrowTip) / 2;
});

const fillColor = computed(() => {
  switch (props.lesson.status) {
    case 'completed': return '#fbbf24'; // yellow like reference
    case 'current': return '#3b82f6';   // blue
    case 'locked': return '#ef4444';
    case 'skipped': return '#fed7aa';   // light orange
    default: return '#ffffff';
  }
});

const strokeColor = computed(() => {
  switch (props.lesson.status) {
    case 'completed': return '#d97706';
    case 'current': return '#1d4ed8';
    case 'locked': return '#b91c1c';
    case 'skipped': return '#f97316';   // orange
    default: return '#9ca3af';
  }
});

const textColor = computed(() => {
  switch (props.lesson.status) {
    case 'completed': return '#78350f';
    case 'current': return '#ffffff';
    case 'locked': return '#ffffff';
    case 'skipped': return '#7c2d12';   // dark orange
    default: return '#374151';
  }
});

const displayText = computed(() => {
  if (props.lesson.status === 'completed') return '\u{1F3C6}'; // trophy
  if (props.lesson.status === 'locked') return '\u{1F512}'; // lock
  if (props.lesson.status === 'skipped') return '!';
  return `${props.index + 1}`;
});
</script>
